"use client"

import * as React from "react"
import { useToast } from "@/hooks/use-toast"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast"
import { CheckCircle2, Info, XCircle, AlertTriangle } from "lucide-react"
import { cn } from "@/lib/utils"

export function Toaster() {
  const { toasts, dismiss } = useToast()
  const [expandedToasts, setExpandedToasts] = React.useState<Set<string>>(new Set())
  const expandTimersRef = React.useRef<Map<string, NodeJS.Timeout>>(new Map())

  const toggleExpand = (id: string) => {
    setExpandedToasts((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
        // Clear timer if collapsing
        const timer = expandTimersRef.current.get(id)
        if (timer) {
          clearTimeout(timer)
          expandTimersRef.current.delete(id)
        }
      } else {
        next.add(id)
        // Set timer to dismiss after 3 seconds when expanded
        const timer = setTimeout(() => {
          dismiss(id)
          expandTimersRef.current.delete(id)
        }, 3000)
        expandTimersRef.current.set(id, timer)
      }
      return next
    })
  }

  // Cleanup timers on unmount
  React.useEffect(() => {
    const timers = expandTimersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer))
      timers.clear()
    }
  }, [])

  const getIcon = (variant?: string | null, size: string = "h-4 w-4") => {
    switch (variant) {
      case "success":
        return <CheckCircle2 className={size} />
      case "destructive":
      case "error":
        return <XCircle className={size} />
      case "warning":
        return <AlertTriangle className={size} />
      case "info":
        return <Info className={size} />
      default:
        return <Info className={size} />
    }
  }

  return (
    <ToastProvider>
      {toasts.map(function ({ id, title, description, action, variant, ...props }) {
        const isExpanded = expandedToasts.has(id)

        return (
          <Toast key={id} {...props} className={cn(
            "relative", 
            !isExpanded && "p-0 border-0 bg-transparent shadow-none w-auto sm:w-full rounded-none sm:p-0 sm:border-0 sm:bg-transparent sm:shadow-none",
            isExpanded && "p-0 border-0 bg-transparent shadow-none w-auto sm:w-full sm:p-0 sm:border-0 sm:bg-transparent sm:shadow-none sm:rounded-none"
          )}>
            {/* Mobile: Expandable bubble */}
            <div className="block sm:hidden">
              {!isExpanded ? (
                // Collapsed: simple circle button
                <button
                  onClick={() => toggleExpand(id)}
                  className="flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-transform duration-200 ease-out hover:scale-110 active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/50 shrink-0 border-0"
                  style={{
                    backgroundColor: variant === "success" 
                      ? "rgb(34, 197, 94)" 
                      : variant === "destructive" || variant === "error"
                      ? "rgb(239, 68, 68)"
                      : variant === "warning"
                      ? "rgb(234, 179, 8)"
                      : variant === "info"
                      ? "rgb(59, 130, 246)"
                      : variant === "default"
                      ? "rgb(100, 116, 139)"
                      : "rgb(100, 116, 139)",
                    color: "white",
                  }}
                >
                  {getIcon(variant, "h-5 w-5")}
                </button>
              ) : (
                // Expanded: full toast with fade-in animation
                <div 
                  className="w-full min-w-[280px] rounded-lg border p-3 pr-8 shadow-lg"
                  style={{
                    backgroundColor: variant === "success"
                      ? "rgb(34, 197, 94)"
                      : variant === "destructive" || variant === "error"
                      ? "rgb(239, 68, 68)"
                      : variant === "warning"
                      ? "rgb(234, 179, 8)"
                      : variant === "info"
                      ? "rgb(59, 130, 246)"
                      : "rgb(100, 116, 139)",
                    borderColor: variant === "success"
                      ? "rgb(34, 197, 94)"
                      : variant === "destructive" || variant === "error"
                      ? "rgb(239, 68, 68)"
                      : variant === "warning"
                      ? "rgb(234, 179, 8)"
                      : variant === "info"
                      ? "rgb(59, 130, 246)"
                      : "rgb(100, 116, 139)",
                    color: "white",
                    animation: "toastExpand 0.2s ease-out",
                  }}
                >
                  <div className="flex items-start justify-between gap-2 w-full">
                    <div className="flex-1 grid gap-0.5 min-w-0 text-left">
                      {title && <ToastTitle className="text-left break-words">{title}</ToastTitle>}
                      {description && (
                        <ToastDescription className="text-left break-words">{description}</ToastDescription>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {action}
                      <ToastClose />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Desktop: Green toast with fade-in */}
            <div className="hidden sm:block w-full">
              <div 
                className="rounded-lg border p-4 pr-8 shadow-lg animate-in fade-in duration-300 min-w-[320px] w-full"
                style={{
                  backgroundColor: variant === "success"
                    ? "rgb(34, 197, 94)"
                    : variant === "destructive" || variant === "error"
                    ? "rgb(239, 68, 68)"
                    : variant === "warning"
                    ? "rgb(234, 179, 8)"
                    : variant === "info"
                    ? "rgb(59, 130, 246)"
                    : "rgb(100, 116, 139)",
                  borderColor: variant === "success"
                    ? "rgb(34, 197, 94)"
                    : variant === "destructive" || variant === "error"
                    ? "rgb(239, 68, 68)"
                    : variant === "warning"
                    ? "rgb(234, 179, 8)"
                    : variant === "info"
                    ? "rgb(59, 130, 246)"
                    : "rgb(100, 116, 139)",
                  color: "white",
                }}
              >
                <div className="flex items-start justify-between gap-2 w-full">
                  <div className="flex items-center gap-2 flex-1">
                    {getIcon(variant, "h-5 w-5")}
                    <div className="grid gap-0.5 flex-1">
                      {title && <ToastTitle className="text-white">{title}</ToastTitle>}
                      {description && (
                        <ToastDescription className="text-white/90">{description}</ToastDescription>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {action}
                    <ToastClose />
                  </div>
                </div>
              </div>
            </div>
          </Toast>
        )
      })}
      <ToastViewport />
    </ToastProvider>
  )
}
